// src/common/pages/authorValidation/datos.ts
/**
 * Lo que la pantalla en pasos hace con los datos, sin pantalla: qué hay
 * guardado, qué falta para una documentación, cómo queda lo leído del
 * documento y qué se envía.
 */
import { isCalendarDate } from "../../calendarDate";
import { cuitContieneDni, dniDeCuit, esCuitValido } from "../dniUpload/cuit";
import { dniEquals, normalizeName } from "../dniUpload/dniMatch";
import { filaDeNacionalidad } from "../dniUpload/nacionalidad";
import type { ValoresLeidos } from "../dniUpload/valoresDeLaLectura";
import type { CampoConfig, FilaConfirmacion, Idioma, OpcionDeCatalogo, Valores } from "./tipos";

/**
 * Los datos que un autor necesita para entrar en una documentación. Espejo de
 * REQUIRED_AUTHOR_FIELDS (jupiter-server, src/common/authors/
 * documentationReadiness.ts) más nombre y apellido, que siguen obligatorios en
 * el alta. El cliente no puede importar código del server: si cambia allá,
 * cambia acá (y la prueba que fija esta lista).
 *
 * El email queda afuera a propósito, aunque el server lo cuenta: la pantalla
 * se abre desde ese email y es obligatorio desde el alta, así que nunca falta
 * acá; la salida manual lo agrega aparte.
 */
export const CAMPOS_PARA_DOCUMENTACION = [
  "firstName",
  "lastName",
  "gender",
  "taxIdType",
  "taxId",
  "nationality",
  "dateOfBirth",
  "streetAndNumber",
  "postalCode",
  "country",
  "province",
] as const;

/**
 * Lo que muestra el resumen final, en este orden, y lo que deja modificar: los
 * datos del documento, el domicilio completo en el orden del formulario de autor
 * (calle, piso, departamento, código postal, localidad, provincia, país) y el
 * teléfono. Se muestra lo que va a quedar: lo leído del DNI y lo escrito a mano
 * encima de lo guardado (KAN-462).
 */
export const CAMPOS_DEL_RESUMEN = [
  "firstName",
  "lastName",
  "dateOfBirth",
  "gender",
  "taxIdType",
  "taxId",
  "nationality",
  "streetAndNumber",
  "floor",
  "suite",
  "postalCode",
  "city",
  "province",
  "country",
  "phone",
] as const;

/**
 * Lo que la pantalla pública no muestra ni envía, aunque el registro lo deje
 * editar: el seudónimo y el IPI (KAN-458). Quedan para la editorial, en el
 * formulario de autor. Como no viajan, lo guardado en ellos no se toca.
 */
export const FUERA_DE_LA_PANTALLA: readonly string[] = ["aka", "ipi"];

/** El domicilio, en una línea y en el orden del formulario de autor. */
const DOMICILIO_EN_UNA_LINEA = ["streetAndNumber", "floor", "suite", "postalCode", "city", "province", "country"] as const;

export function estaVacio(valor: unknown): boolean {
  return valor === null || valor === undefined || String(valor).trim() === "";
}

export function etiquetaDeCampo(campo: CampoConfig | undefined, idioma: Idioma): string {
  if (!campo) return "";
  return campo.label[idioma] || campo.label.es || campo.name;
}

function fechaVisible(iso: string): string {
  const [anio, mes, dia] = iso.slice(0, 10).split("-");
  return anio && mes && dia ? `${dia}/${mes}/${anio}` : iso;
}

/** Cómo se lee un valor: la etiqueta de la opción, el nombre de la fila o la fecha DD/MM/AAAA. */
export function textoDeValor(
  campo: CampoConfig,
  valor: string,
  catalogos: Record<string, OpcionDeCatalogo[]>,
  idioma: Idioma
): string {
  if (campo.type === "Enum") {
    const opcion = campo.options?.find((o) => o.value === valor);
    return opcion ? opcion.label[idioma] || opcion.label.es || valor : valor;
  }
  if (campo.type === "Ref") {
    return catalogos[campo.refEntity ?? ""]?.find((o) => o.id === valor)?.displayId ?? valor;
  }
  if (campo.type === "Date") return fechaVisible(valor);
  return valor;
}

/**
 * Lo guardado del autor, en la forma del formulario: las referencias por id y lo
 * demás como texto. La fecha llega de la ruta como el día de calendario,
 * «AAAA-MM-DD», sin hora, y queda tal cual.
 */
export function valoresGuardados(entityData: Record<string, any>, campos: CampoConfig[]): Valores {
  const valores: Valores = {};
  for (const campo of campos) {
    const crudo = campo.type === "Ref" ? entityData[`${campo.name}_id`] : entityData[campo.name];
    valores[campo.name] = crudo === null || crudo === undefined ? "" : String(crudo);
  }
  return valores;
}

export function faltantesParaDocumentacion(valores: Valores): string[] {
  return CAMPOS_PARA_DOCUMENTACION.filter((nombre) => estaVacio(valores[nombre]));
}

/**
 * El domicilio en el orden del formulario de autor: Calle y Número, Piso,
 * Departamento, Código Postal, País, Provincia y Localidad/Barrio.
 */
const ORDEN_DEL_DOMICILIO = ["streetAndNumber", "floor", "suite", "postalCode", "country", "province", "city"] as const;

/** Lo del domicilio que se pide confirmar, obligatorio, si no se leyó del DNI. */
const DOMICILIO_QUE_SE_CONFIRMA: readonly string[] = ["streetAndNumber", "postalCode", "province"];

/** Lo del domicilio que se ofrece, opcional, cuando hay que pedir la calle. */
const OPCIONALES_DEL_DOMICILIO: readonly string[] = ["floor", "suite", "city"];

/**
 * Qué pide el paso que sigue a las fotos, para su título: el teléfono y el código postal; además el
 * domicilio; o además otros datos de la documentación.
 */
export type TipoDelPasoTelefono = "telefono" | "domicilio" | "faltantes";

/**
 * El paso que sigue a las fotos (KAN-462): los campos que muestra, en orden, cuáles son
 * obligatorios y qué pide, para el título.
 *  - Siempre el teléfono y el código postal, obligatorios: el código postal nunca está en el DNI,
 *    y el teléfono es obligatorio solo en la pantalla pública (el modelo lo deja opcional y el
 *    server no lo exige, KAN-456). La pantalla los precarga con lo guardado.
 *  - El domicilio, si hay que pedirlo (`pedirDomicilio`: no se leyó del DNI ni el autor lo
 *    escribió antes), entero y en el orden del formulario de autor, con la calle, el código postal
 *    y la provincia obligatorios y el piso, el departamento y la localidad opcionales. También si
 *    falta la calle (un domicilio leído que el catálogo no ubicó). Con la calle leída, no: el DNI
 *    trae el piso y el departamento en la misma línea, y ofrecerlos aparte invitaría a escribirlos
 *    dos veces.
 *  - El piso y el departamento vacíos que tenían algo guardado, opcionales y en su lugar: el
 *    domicilio leído del DNI que reemplaza al guardado los vacía (valoresDesdeLectura), y el autor
 *    tiene que poder volver a cargarlos (la pantalla los ofrece vacíos, con lo de antes debajo).
 *  - Cualquier otro dato de la documentación que falte, obligatorio y antes que el resto.
 * El orden: lo otro que falta, el teléfono y el domicilio.
 */
export function camposDelPasoTelefono(
  valores: Valores,
  guardados: Valores = {},
  opciones: { pedirDomicilio?: boolean } = {}
): { campos: string[]; obligatorios: string[]; tipo: TipoDelPasoTelefono } {
  const faltan = faltantesParaDocumentacion(valores);
  if (opciones.pedirDomicilio) {
    for (const nombre of DOMICILIO_QUE_SE_CONFIRMA) if (!faltan.includes(nombre)) faltan.push(nombre);
  }
  if (!faltan.includes("postalCode")) faltan.push("postalCode");
  const opcionales = new Set<string>(faltan.includes("streetAndNumber") ? OPCIONALES_DEL_DOMICILIO : []);
  for (const nombre of PISO_Y_DEPARTAMENTO) {
    if (estaVacio(valores[nombre]) && !estaVacio(guardados[nombre])) opcionales.add(nombre);
  }
  const delDomicilio: readonly string[] = ORDEN_DEL_DOMICILIO;
  const otros = faltan.filter((nombre) => !delDomicilio.includes(nombre));
  const domicilio = delDomicilio.filter((nombre) => faltan.includes(nombre) || opcionales.has(nombre));
  const tipo: TipoDelPasoTelefono =
    otros.length > 0 ? "faltantes" : domicilio.some((nombre) => nombre !== "postalCode") ? "domicilio" : "telefono";
  return {
    campos: [...otros, "phone", ...domicilio],
    obligatorios: [...otros, "phone", ...domicilio.filter((nombre) => faltan.includes(nombre))],
    tipo,
  };
}

/** Lo que un domicilio nuevo vacía y la pantalla vuelve a ofrecer, vacío y con lo de antes debajo. */
export const PISO_Y_DEPARTAMENTO = ["floor", "suite"] as const;

/** Un CUIT/CUIL escrito a mano: solo dígitos y separadores (espacios, puntos, guiones y barras). */
const DIGITOS_Y_SEPARADORES = /^[\d\s./-]+$/;

/**
 * La única regla para llevar un CUIT/CUIL a su máscara, XX-XXXXXXXX-X, o null
 * si no se puede. La usan lo leído del documento (valoresDesdeLectura), el
 * formulario antes de validar (llevarALaMascara y validarCampos, en
 * FormularioDeCampos.tsx) y la comparación con lo guardado
 * (equivaleALoGuardado). Nunca da otro número que el escrito:
 *  - Solo se sacan los separadores. Con una letra u otro carácter, null.
 *  - Con once dígitos, se ponen los guiones. El verificador no se mira, como
 *    con uno escrito con guiones.
 *  - Con diez (el DNI de siete cifras sin su cero, que la máscara 99-9999999-9
 *    admite), se completa el DNI con un cero, solo si así el verificador es
 *    correcto (`esCuitValido`). Si no, puede ser un CUIT de once al que le
 *    falta un dígito: null.
 *  - Con otra cantidad de dígitos, null.
 */
export function cuitConMascara(crudo: string): string | null {
  const texto = String(crudo ?? "").trim();
  if (!DIGITOS_Y_SEPARADORES.test(texto)) return null;
  const digitos = texto.replace(/\D/g, "");
  const once = digitos.length === 10 ? `${digitos.slice(0, 2)}0${digitos.slice(2)}` : digitos;
  if (once.length !== 11) return null;
  if (digitos.length === 10 && !esCuitValido(once)) return null;
  return `${once.slice(0, 2)}-${once.slice(2, 10)}-${once.slice(10)}`;
}

/** Calle, provincia y país: sin los tres, el domicilio leído no se aplica. */
const DOMICILIO_MINIMO = ["streetAndNumber", "province", "country"] as const;

/**
 * Como `normalizeName` (sin acentos, en mayúsculas, un espacio entre palabras),
 * pero con los dígitos: en un domicilio la altura cuenta, y `normalizeName` la
 * borraría ("Av. Siempre Viva 742" y "AV SIEMPRE VIVA 100" darían igual).
 */
function normalizarDomicilio(texto: unknown): string {
  return String(texto ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, " ")
    .trim();
}

/**
 * Si `nuevo` es el mismo domicilio que el guardado: la misma calle (normalizada,
 * con la altura), la misma provincia y el mismo país (por id), y la localidad
 * vacía o la misma. Lo usan lo leído del documento (`valoresDesdeLectura`: con
 * un domicilio que no equivale, lo que el DNI no trae —código postal, piso,
 * departamento— es del domicilio viejo) y el resumen modificado
 * (`sinBorrarLaLocalidad`).
 */
export function domicilioEquivale(nuevo: Valores, guardados: Valores): boolean {
  const calle = normalizarDomicilio(nuevo.streetAndNumber);
  return (
    calle !== "" &&
    calle === normalizarDomicilio(guardados.streetAndNumber) &&
    String(nuevo.province ?? "").trim() === (guardados.province ?? "") &&
    String(nuevo.country ?? "").trim() === (guardados.country ?? "") &&
    (estaVacio(nuevo.city) || normalizarDomicilio(nuevo.city) === normalizarDomicilio(guardados.city))
  );
}

/**
 * Lo que un domicilio nuevo deja vacío: el código postal, que se pide en el paso
 * del teléfono, y el piso y el departamento si el formulario los tiene (viajan
 * como null). `guardados` trae una clave por cada campo del registro
 * (`valoresGuardados`), así que una clave presente dice que el campo existe.
 */
function vaciosDelDomicilioViejo(guardados: Valores): Valores {
  const vacios: Valores = { postalCode: "" };
  for (const nombre of ["floor", "suite"]) {
    if (nombre in guardados) vacios[nombre] = "";
  }
  return vacios;
}

/**
 * Lo que viaja al enviar: lo leído del documento y, encima, lo que el autor dio a mano (en el
 * paso del teléfono o al modificar el resumen). Un dato dado igual a lo guardado no viaja, y
 * tampoco lo leído en ese campo: queda lo guardado (recorrido 4: lo guardado que el autor vio y no
 * tocó no se reenvía). Así el código postal guardado que el autor vuelve a escribir, después de que
 * el domicilio leído lo vaciara, queda como estaba. Lo leído viaja como siempre; lo que equivale a
 * lo guardado lo saca `armarEnvio`.
 */
export function valoresQueViajan(leidos: Valores, dados: Valores, guardados: Valores): Valores {
  const salida: Valores = { ...leidos };
  for (const [nombre, valor] of Object.entries(dados)) {
    if (String(valor ?? "").trim() === String(guardados[nombre] ?? "").trim()) delete salida[nombre];
    else salida[nombre] = valor;
  }
  return salida;
}

/**
 * Lo dado a mano que sigue en pie después de volver a leer el documento (otra foto): lo de los
 * campos que la lectura nueva no cambió. Un campo que la foto nueva cambió muestra lo de la foto,
 * para confirmarlo. `leidosAntes` es lo leído cuando el autor los dio.
 */
export function dadosVigentes(dados: Valores, leidosAntes: Valores, leidosAhora: Valores): Valores {
  return Object.fromEntries(
    Object.entries(dados).filter(([nombre]) => String(leidosAhora[nombre] ?? "") === String(leidosAntes[nombre] ?? ""))
  );
}

/**
 * La localidad guardada no se borra sin querer: si el envío deja el mismo domicilio que el guardado
 * (`domicilioEquivale`, que acepta la localidad vacía) pero sin localidad, y la guardada tenía una,
 * la localidad no viaja. Pasa cuando el DNI reemplazó el domicilio sin traer la localidad y el autor,
 * al modificar, vuelve a escribir su calle. La pantalla lo aplica solo si el autor no tocó la
 * localidad: si la borró él, viaja vacía. No modifica `envio`.
 */
export function sinBorrarLaLocalidad(envio: Valores, guardados: Valores): Valores {
  const queda = { ...guardados, ...envio };
  if (!estaVacio(queda.city) || estaVacio(guardados.city) || !domicilioEquivale(queda, guardados)) return envio;
  const { city: _localidad, ...sinLocalidad } = envio;
  return sinLocalidad;
}

/**
 * Espejo de SOLO_DE_NOMBRE de jupiter-server
 * (src/common/dniUpload/dniServerVerification.ts): lo que puede tener un nombre,
 * ya sin acentos (letras, espacio, ' o ’ y guion). El server le pone el tic a un
 * nombre guardado solo si pasa esto; si cambia allá, cambia acá.
 */
const SOLO_DE_NOMBRE = /^[A-Za-z '\u2019-]*$/;

/** Si el server le puede poner el tic a este nombre tal como está (ver SOLO_DE_NOMBRE). */
function nombreConTic(texto: string): boolean {
  return SOLO_DE_NOMBRE.test(texto.normalize("NFD").replace(/[\u0300-\u036f]/g, ""));
}

/**
 * Lo leído del documento, listo para confirmar.
 *
 * El número va con su tipo. Si el autor ya tiene cargado un CUIT válido que
 * contiene el DNI del documento, ese CUIT se conserva: el documento solo
 * prueba el número del medio, y pisar un CUIT correcto con el DNI pelado
 * perdería el prefijo real. Un CUIL del documento solo vale como CUIL si pasa
 * el verificador; si no, se toma el DNI que lleva adentro. Un CUIT guardado que
 * la regla de la máscara no acepta (`cuitConMascara`: un dato viejo de diez
 * dígitos que no cierra), escrito solo con dígitos y separadores, y que
 * contiene el DNI del documento queda como está: no es un cambio del autor
 * (`sinCambiar`). Con una letra u otro carácter, va el DNI. La nacionalidad llega
 * en el código del MRZ (ARG) y se busca en el catálogo por el código del país
 * (`filaDeNacionalidad`); si no está, o el catálogo no trae códigos y el nombre
 * no distingue una fila, queda para el paso del teléfono, que pide lo que falta.
 *
 * El domicilio se aplica entero o no se aplica: solo si lo leído trae calle,
 * provincia y país. Cuando se aplica, reemplaza el domicilio guardado entero:
 * además de esos tres vuelve la localidad (la leída, o vacía si no vino) y el
 * código postal vacío, que el documento no trae. Sin eso, la pantalla, que
 * mezcla `{ ...guardados, ...valores }`, dejaría la localidad y el código
 * postal del domicilio viejo junto a una calle nueva; vacíos, el código postal
 * se pide en el paso del teléfono. Si lo leído viene parcial no se vuelca
 * ningún dato del domicilio, ni siquiera para vaciar, y quedan los guardados, o
 * lo pide el paso del teléfono. El domicilio no lleva tic de validado, y mezclar dos
 * domicilios es peor que pedirlo.
 *
 * Cuando el domicilio leído reemplaza al guardado, también el piso y el
 * departamento vuelven vacíos (si el formulario los tiene): el DNI no los trae,
 * y los del domicilio viejo junto a la calle nueva serían otra mezcla.
 *
 * Lo leído que equivale a lo guardado no pisa lo guardado. Pisarlo con la forma
 * del documento (mayúsculas, sin acentos) borra un dato correcto, y el server lo
 * cuenta como un cambio, que bloquea si hay documentación en curso:
 *  - el nombre y el apellido iguales con la normalización del documento
 *    (`normalizeName`) quedan como están guardados ("Ana María", no
 *    "ANA MARIA"), pero solo si el server le puede poner el tic a lo guardado
 *    (SOLO_DE_NOMBRE): "Pérez." o "Juan Carlos 2" no lo llevan, y conservarlos
 *    perdería el tic que lo leído sí lleva. Lo guardado se mira recortado, como
 *    lo ve el server (el envío recorta todo): "Pérez\t" queda "Pérez";
 *  - el número de un DU guardado con los mismos dígitos (sin separadores ni
 *    ceros a la izquierda) queda tal cual ("12.345.678");
 *  - el domicilio leído que equivale al guardado (`domicilioEquivale`) no se
 *    vuelca: quedan el domicilio, el código postal, el piso y el departamento
 *    guardados, y el código postal no se pide. Si algo difiere, se reemplaza
 *    entero, como arriba.
 * La fecha leída sí queda, como «AAAA-MM-DD»: la del mismo día que la guardada
 * no viaja, y el DU o el CUIT/CUIL con los mismos dígitos tampoco (ver
 * `armarEnvio`).
 */
export function valoresDesdeLectura(
  lectura: ValoresLeidos,
  guardados: Valores,
  catalogos: Record<string, OpcionDeCatalogo[]>
): Valores {
  const v = lectura.valores;
  const salida: Valores = {};
  const copiarLeidos = (nombres: readonly string[]) => {
    for (const nombre of nombres) {
      if (!estaVacio(v[nombre])) salida[nombre] = String(v[nombre]).trim();
    }
  };
  for (const nombre of ["firstName", "lastName"]) {
    if (estaVacio(v[nombre])) continue;
    const leido = String(v[nombre]).trim();
    // Recortado, como lo ve el server: el envío recorta todo (armarEnvio) y la ruta guarda así.
    const guardado = (guardados[nombre] ?? "").trim();
    const equivalente =
      !estaVacio(guardado) &&
      nombreConTic(guardado) &&
      normalizeName(leido) !== "" &&
      normalizeName(leido) === normalizeName(guardado);
    salida[nombre] = equivalente ? guardado : leido;
  }
  copiarLeidos(["dateOfBirth", "gender"]);
  if (DOMICILIO_MINIMO.every((nombre) => !estaVacio(v[nombre])) && !domicilioEquivale(v, guardados)) {
    copiarLeidos(DOMICILIO_MINIMO);
    salida.city = estaVacio(v.city) ? "" : String(v.city).trim();
    Object.assign(salida, vaciosDelDomicilioViejo(guardados));
  }
  const leido = String(v.taxId ?? "").replace(/\D/g, "");
  const numero = v.taxIdType === "cuitCuil" ? dniDeCuit(leido) ?? "" : leido;
  const cuitGuardadoCrudo = guardados.taxIdType === "cuitCuil" ? (guardados.taxId ?? "").trim() : "";
  const cuitGuardado = cuitGuardadoCrudo ? cuitConMascara(cuitGuardadoCrudo) : null;
  const cuitLeido = v.taxIdType === "cuitCuil" && leido.length === 11 ? cuitConMascara(leido) : null;
  if (numero && cuitGuardado && esCuitValido(cuitGuardado) && cuitContieneDni(cuitGuardado, numero)) {
    salida.taxIdType = "cuitCuil";
    salida.taxId = cuitGuardado;
  } else if (cuitLeido && esCuitValido(cuitLeido)) {
    salida.taxIdType = "cuitCuil";
    salida.taxId = cuitLeido;
  } else if (
    numero &&
    cuitGuardadoCrudo &&
    cuitGuardado === null &&
    DIGITOS_Y_SEPARADORES.test(cuitGuardadoCrudo) &&
    cuitContieneDni(cuitGuardadoCrudo, numero)
  ) {
    // Un CUIT viejo que la regla no lleva a la máscara (diez dígitos que no cierran con el
    // verificador), y que la app y el server aceptan, con el DNI del documento adentro: queda como
    // está guardado. No es un cambio del autor: no se valida ni viaja (sinCambiar), y queda sin tic.
    // Solo si son dígitos y separadores: con una letra u otro carácter no es un número de CUIT, y lo
    // reemplaza el DNI del documento, como antes.
    salida.taxIdType = "cuitCuil";
    salida.taxId = cuitGuardadoCrudo;
  } else if (numero) {
    salida.taxIdType = "du";
    const duGuardado = guardados.taxIdType === "du" ? guardados.taxId ?? "" : "";
    salida.taxId = duGuardado && dniEquals(duGuardado, numero) ? duGuardado : numero;
  }
  const nacionalidad = filaDeNacionalidad(catalogos.Country, v.nationality);
  if (nacionalidad) salida.nationality = nacionalidad.id;
  return salida;
}

/** El tipo de documento con que queda el número: el que se envía o, si no se envía, el guardado. */
function tipoQueQueda(valores: Valores, guardado: Record<string, unknown>): unknown {
  return "taxIdType" in valores ? valores.taxIdType : guardado.taxIdType;
}

/** Los dígitos de un número escrito con dígitos y separadores, o null si tiene otra cosa. */
function digitosDe(texto: string): string | null {
  return DIGITOS_Y_SEPARADORES.test(texto) ? texto.replace(/\D/g, "") : null;
}

/**
 * Si el autor no cambió este dato, frente a lo guardado crudo (`entityData`):
 *  - el número (CUIT/CUIL, DU u otro), con el mismo tipo de documento y el
 *    mismo texto, o los mismos dígitos (solo con separadores, sin letras);
 *  - la fecha, el mismo día que la guardada: los dos textos «AAAA-MM-DD».
 * Lo que no cambió no se valida (validarCampos) ni viaja (armarEnvio): un dato
 * viejo que la app y el server aceptaron, pero que no cumple las reglas de
 * esta pantalla (un CUIT de diez dígitos que no cierra con el verificador), no
 * frena la confirmación; queda como estaba, sin tic. Lo que el autor escribe o
 * cambia se valida como siempre. Los demás datos no entran: se validan siempre.
 */
export function sinCambiar(
  nombre: string,
  valor: string,
  valores: Valores,
  campos: CampoConfig[],
  guardado: Record<string, unknown>
): boolean {
  if (campos.find((c) => c.name === nombre)?.type === "Date") {
    const dia = valor.trim();
    return isCalendarDate(dia) && dia === guardado[nombre];
  }
  if (nombre !== "taxId") return false;
  if (tipoQueQueda(valores, guardado) !== guardado.taxIdType || typeof guardado.taxId !== "string") return false;
  const escrito = valor.trim();
  const crudo = guardado.taxId.trim();
  if (escrito === "") return false;
  if (escrito === crudo) return true;
  const digitos = digitosDe(escrito);
  return digitos !== null && digitos !== "" && digitos === digitosDe(crudo);
}

/**
 * Si un valor equivale a lo guardado aunque su forma sea otra. El server compara
 * el texto del número y contaría como cambio un dato que el autor no cambió: lo
 * anota en la validación y, si el autor está en una documentación en curso,
 * rechaza la confirmación entera. La fecha no tiene otra forma: es el día,
 * «AAAA-MM-DD», y equivale solo si es el mismo (`sinCambiar`).
 *  - El número de un DU que sigue siendo DU: los mismos dígitos (`dniEquals`,
 *    sin separadores ni ceros a la izquierda). Un DU viejo guardado como
 *    «12.345.678» se corrige en dígitos (valoresParaCorregir).
 *  - El número de un CUIT/CUIL que sigue siendo CUIT/CUIL: el mismo, con las
 *    dos formas llevadas a la máscara con la misma regla (`cuitConMascara`).
 *    Uno viejo guardado sin guiones («20123456786») la pantalla lo muestra y lo
 *    manda con ellos (valoresDesdeLectura, llevarALaMascara). Solo equivale un
 *    guardado de once dígitos: uno de diez (el DNI de siete cifras sin su cero)
 *    el server no lo prueba (documentNumberProvenByTaxId); completado a once,
 *    viaja, repara el dato y el server le puede dar el tic.
 * Se compara contra lo guardado crudo (`entityData`). Lo que no cambió
 * (`sinCambiar`) también equivale.
 */
function equivaleALoGuardado(
  nombre: string,
  valor: string,
  valores: Valores,
  campos: CampoConfig[],
  guardado: Record<string, unknown>
): boolean {
  if (sinCambiar(nombre, valor, valores, campos, guardado)) return true;
  if (nombre === "taxId") {
    const tipo = tipoQueQueda(valores, guardado);
    const numeroGuardado = guardado.taxId;
    if (tipo === "cuitCuil" && guardado.taxIdType === "cuitCuil") {
      const cuitGuardado = typeof numeroGuardado === "string" ? numeroGuardado : "";
      const guardadoConMascara = cuitConMascara(cuitGuardado);
      return (
        guardadoConMascara !== null &&
        cuitGuardado.replace(/\D/g, "").length === 11 &&
        guardadoConMascara === cuitConMascara(valor)
      );
    }
    return (
      tipo === "du" && guardado.taxIdType === "du" && typeof numeroGuardado === "string" && dniEquals(numeroGuardado, valor)
    );
  }
  return false;
}

/**
 * El cuerpo de `data` del envío: solo los campos de `editables` (la pantalla
 * pasa los suyos, sin el seudónimo ni el IPI: FUERA_DE_LA_PANTALLA) y lo vacío
 * como null (dos vacíos no chocan en un índice único).
 *
 * Lo que equivale a lo guardado no viaja (`equivaleALoGuardado`), en todos los
 * caminos de la pantalla: el resumen tal cual, el resumen modificado y la carga
 * a mano. Las marcas no dependen de eso: el server las calcula releyendo el autor
 * guardado.
 *
 * Una fecha nueva o cambiada viaja como su día, «AAAA-MM-DD», sin hora: es un
 * día de calendario y el server lo guarda así, el mismo en cualquier zona.
 *
 * `guardado` es lo guardado del autor tal como llega (`info.entityData`).
 */
export function armarEnvio(
  editables: string[],
  valores: Valores,
  campos: CampoConfig[],
  guardado: Record<string, unknown>
): Record<string, string | null> {
  const permitidos = new Set(editables);
  const conocidos = new Set(campos.map((c) => c.name));
  const envio: Record<string, string | null> = {};
  for (const [nombre, valor] of Object.entries(valores)) {
    if (!permitidos.has(nombre) || !conocidos.has(nombre)) continue;
    if (estaVacio(valor)) {
      envio[nombre] = null;
      continue;
    }
    if (equivaleALoGuardado(nombre, valor, valores, campos, guardado)) continue;
    envio[nombre] = valor.trim();
  }
  return envio;
}

/**
 * El domicilio guardado que el leído del DNI reemplaza, en una línea, o null si
 * no reemplaza ninguno (no se leyó, o no había guardado). `leidos` trae el
 * domicilio solo cuando es otro que el guardado (valoresDesdeLectura).
 */
export function domicilioReemplazado(
  leidos: Valores,
  guardados: Valores,
  campos: CampoConfig[],
  catalogos: Record<string, OpcionDeCatalogo[]>,
  idioma: Idioma
): string | null {
  if (estaVacio(leidos.streetAndNumber) || estaVacio(guardados.streetAndNumber)) return null;
  const porNombre = new Map(campos.map((c) => [c.name, c] as const));
  return DOMICILIO_EN_UNA_LINEA.filter((n) => !estaVacio(guardados[n]))
    .map((n) => {
      const campo = porNombre.get(n);
      return campo ? textoDeValor(campo, guardados[n], catalogos, idioma) : String(guardados[n]);
    })
    .join(", ");
}

/**
 * Las filas del resumen («¿Son correctos tus datos?»): lo que va a quedar (lo
 * guardado con lo leído y lo dado a mano encima), en el orden de `nombres`: por
 * defecto, los datos del documento, el domicilio completo y el teléfono
 * (CAMPOS_DEL_RESUMEN). Lo vacío no se muestra.
 */
export function filasDeConfirmacion(
  valores: Valores,
  campos: CampoConfig[],
  catalogos: Record<string, OpcionDeCatalogo[]>,
  idioma: Idioma,
  nombres: readonly string[] = CAMPOS_DEL_RESUMEN
): FilaConfirmacion[] {
  const porNombre = new Map(campos.map((c) => [c.name, c] as const));
  const filas: FilaConfirmacion[] = [];
  for (const nombre of nombres) {
    if (nombre === "taxIdType") continue;
    const campo = porNombre.get(nombre);
    const valor = valores[nombre];
    if (!campo || estaVacio(valor)) continue;
    let etiqueta = etiquetaDeCampo(campo, idioma);
    // El número se rotula con su tipo: "DU (DNI)" o "CUIT/CUIL" dicen qué es.
    const tipo = porNombre.get("taxIdType");
    if (nombre === "taxId" && tipo && !estaVacio(valores.taxIdType)) {
      etiqueta = textoDeValor(tipo, valores.taxIdType, catalogos, idioma);
    }
    filas.push({ campo: nombre, etiqueta, texto: textoDeValor(campo, valor, catalogos, idioma) });
  }
  return filas;
}

export function soloCampos(valores: Valores, nombres: readonly string[]): Valores {
  const salida: Valores = {};
  for (const nombre of nombres) {
    if (nombre in valores) salida[nombre] = valores[nombre];
  }
  return salida;
}
